import { Select, SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import IUser, { USER_ROLES, UserRoleType } from "@/interfaces/IUser";
import { ISelectOption } from "@/interfaces/Select";
import {
  SignedConventionStatsGroupBy,
  SignedConventionStatsPeriod,
} from "@/interfaces/SignedConventionStats";
import { FC } from "react";
import { ButtonGroup, Card, Form, ToggleButton } from "react-bootstrap";
import { MultiValue } from "react-select";

const SELECTABLE_ROLES = USER_ROLES.filter((r) =>
  ["AE", "SDR"].includes(r.value),
);

export interface SignedConventionStatsFilterState {
  startDate: string;
  endDate: string;
  period: SignedConventionStatsPeriod;
  groupBy: SignedConventionStatsGroupBy;
  users: IUser[];
  roles: ISelectOption<UserRoleType>[];
}

interface Props {
  filters: SignedConventionStatsFilterState;
  onChange: (patch: Partial<SignedConventionStatsFilterState>) => void;
  onReset: () => void;
}

const PERIODS: { value: SignedConventionStatsPeriod; label: string }[] = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
];

const GROUP_BYS: { value: SignedConventionStatsGroupBy; label: string }[] = [
  { value: "user", label: "Utilisateur" },
  { value: "role", label: "Rôle" },
];

const SignedConventionStatsFilters: FC<Props> = ({
  filters,
  onChange,
  onReset,
}) => {
  const { loadUsersOptions } = useLoadOptions();

  return (
    <Card className="mb-3 shadow-none border">
      <Card.Body>
        <Form>
          <div className="row g-3">
            <Form.Group className="col-md-3">
              <Form.Label className="mb-1 fs--1">Du</Form.Label>
              <Form.Control
                type="date"
                value={filters.startDate}
                onChange={(e) => onChange({ startDate: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label className="mb-1 fs--1">Au</Form.Label>
              <Form.Control
                type="date"
                value={filters.endDate}
                onChange={(e) => onChange({ endDate: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="col-md-3">
              <Form.Label className="mb-1 fs--1 d-block">Période</Form.Label>
              <ButtonGroup>
                {PERIODS.map((p) => (
                  <ToggleButton
                    key={p.value}
                    id={`period-${p.value}`}
                    type="radio"
                    variant="outline-primary"
                    size="sm"
                    name="period"
                    value={p.value}
                    checked={filters.period === p.value}
                    onChange={() => onChange({ period: p.value })}
                  >
                    {p.label}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Form.Group>

            <Form.Group className="col-md-3">
              <Form.Label className="mb-1 fs--1 d-block">
                Grouper par
              </Form.Label>
              <ButtonGroup>
                {GROUP_BYS.map((g) => (
                  <ToggleButton
                    key={g.value}
                    id={`groupBy-${g.value}`}
                    type="radio"
                    variant="outline-primary"
                    size="sm"
                    name="groupBy"
                    value={g.value}
                    checked={filters.groupBy === g.value}
                    onChange={() => onChange({ groupBy: g.value })}
                  >
                    {g.label}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Form.Group>

            <Form.Group className="col-md-6">
              <Form.Label className="mb-1 fs--1">Utilisateurs</Form.Label>
              <SelectPaginateAsync
                loadOptions={loadUsersOptions}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname}`
                }
                getOptionValue={(option) => option.id.toString()}
                value={filters.users}
                isMulti
                isClearable
                onChange={(users) =>
                  onChange({ users: (users as MultiValue<IUser>).slice() })
                }
              />
            </Form.Group>

            <Form.Group className="col-md-6">
              <Form.Label className="mb-1 fs--1">Rôles</Form.Label>
              <Select
                options={SELECTABLE_ROLES}
                value={filters.roles}
                isMulti
                isClearable
                onChange={(roles) =>
                  onChange({
                    roles: (
                      roles as MultiValue<ISelectOption<UserRoleType>>
                    ).slice(),
                  })
                }
              />
            </Form.Group>

            <div className="col-12 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onReset}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SignedConventionStatsFilters;
